import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/apiService';

const REPORT_OPTIONS = [
  {
    value: 'selfPickup',
    label: 'Selbstabholerliste',
    description: 'Gesamtübersicht der Selbstabholungen und Rücknahmen eines Tages.',
  },
  {
    value: 'transport',
    label: 'Transportliste',
    description: 'Gesamtübersicht der geplanten Transporte eines Tages.',
  },
  {
    value: 'workshopAssignment',
    label: 'WA-Einsatzliste',
    description: 'Gesamtübersicht der Werkstattaufträge und Mitarbeitereinsätze.',
  },
  {
    value: 'inspections',
    label: 'Prüftermine',
    description: 'Gesamtübersicht anstehender Prüfungen in einem Zeitraum.',
  },
];

const INSPECTION_TYPES = [
  { value: 'uvvInspection', label: 'UVV' },
  { value: 'tuvInspection', label: 'TÜV' },
  { value: 'tachoInspection', label: 'Tacho' },
  { value: 'asuInspection', label: 'ASU' },
  { value: 'otherInspection', label: 'Sonstige' },
];

const ITEM_TYPES = [
  { value: 'rental', label: 'Mietartikel' },
  { value: 'sale', label: 'Verkauf' },
  { value: 'service', label: 'Service' },
  { value: 'sparePart', label: 'Ersatzteil' },
  { value: 'transportvehicle', label: 'Transportfahrzeug' },
  { value: 'inventory', label: 'Inventar' },
];

const formatDateInput = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLabel = value => {
  if (!value) return '-';
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
};

const createDefaultCriteria = () => {
  const today = new Date();
  const monthLater = new Date(today);
  monthLater.setDate(monthLater.getDate() + 30);
  return {
    date: formatDateInput(today),
    driverId: '',
    employeeId: '',
    dateFrom: formatDateInput(today),
    dateTo: formatDateInput(monthLater),
    inspectionTypes: INSPECTION_TYPES.map(option => option.value),
    itemTypes: ITEM_TYPES.map(option => option.value),
    storageLocationId: '',
  };
};

const employeeLabel = employee =>
  [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim()
  || `Mitarbeiter #${employee?.id}`;

const toggleArrayValue = (values, value) =>
  values.includes(value)
    ? values.filter(entry => entry !== value)
    : [...values, value];

const formatPreviewDate = value => {
  if (!value) return '-';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('de-DE');
};

const formatPreviewTime = value => {
  if (!value) return '-';
  const text = String(value);
  const timeMatch = text.match(/(?:T|^)(\d{2}:\d{2})/);
  if (timeMatch) return timeMatch[1];
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? text
    : date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const createInspectionRows = (items, criteria) => {
  const rows = [];
  (items || []).forEach(item => {
    INSPECTION_TYPES.forEach(({ value, label }) => {
      const inspectionDate = item[value];
      if (
        criteria.inspectionTypes.includes(value)
        && inspectionDate
        && inspectionDate >= criteria.dateFrom
        && inspectionDate <= criteria.dateTo
      ) {
        rows.push({
          ...item,
          inspectionType: label,
          inspectionDate,
        });
      }
    });
  });
  return rows.sort((left, right) =>
    left.inspectionDate > right.inspectionDate ? 1 : -1
  );
};

function ReportSelectionModal({
  isOpen,
  initialSelection,
  onClose,
  onApply,
}) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState('');
  const [criteria, setCriteria] = useState(createDefaultCriteria);
  const [drivers, setDrivers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedType(initialSelection?.type || '');
    setCriteria({
      ...createDefaultCriteria(),
      ...(initialSelection?.criteria || {}),
      inspectionTypes:
        initialSelection?.criteria?.inspectionTypes
        || INSPECTION_TYPES.map(option => option.value),
      itemTypes:
        initialSelection?.criteria?.itemTypes
        || ITEM_TYPES.map(option => option.value),
    });
    setError('');
    setPreviewData(null);
    setPreviewError('');
  }, [isOpen, initialSelection]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setOptionsLoading(true);
    Promise.all([
      api.get('/employees', { params: { role: 'driver' } }),
      api.get('/employees'),
      api.get('/storage-locations'),
    ])
      .then(([driverResponse, employeeResponse, locationResponse]) => {
        if (!active) return;
        setDrivers(driverResponse.data || []);
        setEmployees(employeeResponse.data || []);
        setStorageLocations(locationResponse.data || []);
      })
      .catch(fetchError => {
        console.error('Auswahlwerte für den Listenversand konnten nicht geladen werden:', fetchError);
        if (active) {
          setDrivers([]);
          setEmployees([]);
          setStorageLocations([]);
        }
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || step !== 2 || !selectedType) return undefined;

    let active = true;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError('');

      try {
        let data;
        if (selectedType === 'selfPickup') {
          if (!criteria.date) {
            data = [];
          } else {
            const response = await api.get('/servicemarkers/dailyPlanningForm', {
              params: { date: criteria.date },
            });
            data = response.data?.serviceMarkers || [];
          }
        } else if (selectedType === 'transport') {
          if (!criteria.date) {
            data = [];
          } else {
            const params = { date: criteria.date };
            if (criteria.driverId) params.driverId = criteria.driverId;
            const response = await api.get('/transportToday/todayForm', { params });
            data = response.data || [];
          }
        } else if (selectedType === 'workshopAssignment') {
          if (!criteria.date) {
            data = {};
          } else {
            const params = { date: criteria.date };
            if (criteria.employeeId) params.employeeId = criteria.employeeId;
            const response = await api.get('/employeeTimeLogs/dailyPlanningForm', {
              params,
            });
            data = response.data?.groupedEmployees || {};
          }
        } else if (
          !criteria.dateFrom
          || !criteria.dateTo
          || criteria.dateFrom > criteria.dateTo
          || !criteria.inspectionTypes.length
          || !criteria.itemTypes.length
        ) {
          data = [];
        } else {
          const params = { itemTypes: criteria.itemTypes.join(',') };
          if (criteria.storageLocationId) {
            params.storageLocationId = criteria.storageLocationId;
          }
          const response = await api.get('/items', { params });
          data = createInspectionRows(response.data, criteria);
        }

        if (active) setPreviewData(data);
      } catch (fetchError) {
        console.error('Listenvorschau konnte nicht geladen werden:', fetchError);
        if (active) {
          setPreviewData(null);
          setPreviewError('Die Daten für die Listenvorschau konnten nicht geladen werden.');
        }
      } finally {
        if (active) setPreviewLoading(false);
      }
    };

    loadPreview();
    return () => {
      active = false;
    };
  }, [isOpen, step, selectedType, criteria]);

  const selectedReport = useMemo(
    () => REPORT_OPTIONS.find(option => option.value === selectedType),
    [selectedType]
  );

  if (!isOpen) return null;

  const updateCriteria = (key, value) => {
    setCriteria(previous => ({ ...previous, [key]: value }));
    setError('');
  };

  const goToCriteria = () => {
    if (!selectedType) {
      setError('Bitte zuerst eine Liste auswählen.');
      return;
    }
    setStep(2);
    setError('');
  };

  const buildSelection = () => {
    if (selectedType === 'selfPickup') {
      if (!criteria.date) throw new Error('Bitte ein Datum auswählen.');
      return {
        type: selectedType,
        criteria: { date: criteria.date },
        summary: `${selectedReport.label} · ${formatDateLabel(criteria.date)}`,
      };
    }

    if (selectedType === 'transport') {
      if (!criteria.date) throw new Error('Bitte ein Datum auswählen.');
      const driver = drivers.find(
        entry => String(entry.id) === String(criteria.driverId)
      );
      return {
        type: selectedType,
        criteria: {
          date: criteria.date,
          driverId: criteria.driverId || null,
        },
        summary: [
          selectedReport.label,
          formatDateLabel(criteria.date),
          driver ? `Fahrer: ${employeeLabel(driver)}` : 'Alle Fahrer',
        ].join(' · '),
      };
    }

    if (selectedType === 'workshopAssignment') {
      if (!criteria.date) throw new Error('Bitte ein Datum auswählen.');
      const employee = employees.find(
        entry => String(entry.id) === String(criteria.employeeId)
      );
      return {
        type: selectedType,
        criteria: {
          date: criteria.date,
          employeeId: criteria.employeeId || null,
        },
        summary: [
          selectedReport.label,
          formatDateLabel(criteria.date),
          employee ? `Mitarbeiter: ${employeeLabel(employee)}` : 'Alle Mitarbeiter',
        ].join(' · '),
      };
    }

    if (!criteria.dateFrom || !criteria.dateTo) {
      throw new Error('Bitte einen vollständigen Zeitraum auswählen.');
    }
    if (criteria.dateFrom > criteria.dateTo) {
      throw new Error('Das Von-Datum darf nicht nach dem Bis-Datum liegen.');
    }
    if (!criteria.inspectionTypes.length) {
      throw new Error('Bitte mindestens eine Prüfungsart auswählen.');
    }
    if (!criteria.itemTypes.length) {
      throw new Error('Bitte mindestens eine Artikelart auswählen.');
    }
    const location = storageLocations.find(
      entry => String(entry.id) === String(criteria.storageLocationId)
    );
    return {
      type: selectedType,
      criteria: {
        dateFrom: criteria.dateFrom,
        dateTo: criteria.dateTo,
        inspectionTypes: criteria.inspectionTypes,
        itemTypes: criteria.itemTypes,
        storageLocationId: criteria.storageLocationId || null,
      },
      summary: [
        selectedReport.label,
        `${formatDateLabel(criteria.dateFrom)}–${formatDateLabel(criteria.dateTo)}`,
        location ? `Lagerort: ${location.name}` : 'Alle Lagerorte',
      ].join(' · '),
    };
  };

  const applySelection = () => {
    try {
      onApply(buildSelection());
      onClose();
    } catch (selectionError) {
      setError(selectionError.message);
    }
  };

  return (
    <div
      style={styles.overlay}
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
        style={styles.modal}
      >
        <div style={styles.header}>
          <div>
            <h2 id="report-modal-title" style={styles.title}>PDF-Liste hinzufügen</h2>
            <div style={styles.stepText}>Schritt {step} von 2</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Modal schließen"
            style={styles.closeButton}
          >
            ×
          </button>
        </div>

        {step === 1 ? (
          <div>
            <p style={styles.intro}>Welche Gesamtliste soll angehängt werden?</p>
            <div style={styles.cardGrid}>
              {REPORT_OPTIONS.map(option => {
                const selected = selectedType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedType(option.value);
                      setError('');
                    }}
                    style={{
                      ...styles.reportCard,
                      ...(selected ? styles.reportCardSelected : {}),
                    }}
                    aria-pressed={selected}
                  >
                    <strong>{option.label}</strong>
                    <span style={styles.cardDescription}>{option.description}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <p style={styles.intro}>
              Auswahlkriterien für <strong>{selectedReport?.label}</strong>
            </p>

            {selectedType === 'selfPickup' && (
              <Field label="Datum">
                <input
                  type="date"
                  value={criteria.date}
                  onChange={event => updateCriteria('date', event.target.value)}
                  style={styles.input}
                />
              </Field>
            )}

            {selectedType === 'transport' && (
              <>
                <Field label="Datum">
                  <input
                    type="date"
                    value={criteria.date}
                    onChange={event => updateCriteria('date', event.target.value)}
                    style={styles.input}
                  />
                </Field>
                <Field label="Fahrer">
                  <select
                    value={criteria.driverId}
                    onChange={event => updateCriteria('driverId', event.target.value)}
                    style={styles.input}
                    disabled={optionsLoading}
                  >
                    <option value="">Alle Fahrer</option>
                    {drivers.map(driver => (
                      <option key={driver.id} value={driver.id}>
                        {employeeLabel(driver)}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            {selectedType === 'workshopAssignment' && (
              <>
                <Field label="Datum">
                  <input
                    type="date"
                    value={criteria.date}
                    onChange={event => updateCriteria('date', event.target.value)}
                    style={styles.input}
                  />
                </Field>
                <Field label="Mitarbeiter">
                  <select
                    value={criteria.employeeId}
                    onChange={event => updateCriteria('employeeId', event.target.value)}
                    style={styles.input}
                    disabled={optionsLoading}
                  >
                    <option value="">Alle Mitarbeiter</option>
                    {employees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employeeLabel(employee)}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            {selectedType === 'inspections' && (
              <>
                <div style={styles.row}>
                  <Field label="Von">
                    <input
                      type="date"
                      value={criteria.dateFrom}
                      onChange={event => updateCriteria('dateFrom', event.target.value)}
                      style={styles.input}
                    />
                  </Field>
                  <Field label="Bis">
                    <input
                      type="date"
                      value={criteria.dateTo}
                      onChange={event => updateCriteria('dateTo', event.target.value)}
                      style={styles.input}
                    />
                  </Field>
                </div>
                <Field label="Lagerort">
                  <select
                    value={criteria.storageLocationId}
                    onChange={event =>
                      updateCriteria('storageLocationId', event.target.value)
                    }
                    style={styles.input}
                    disabled={optionsLoading}
                  >
                    <option value="">Alle Lagerorte</option>
                    {storageLocations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                        {location.isActive === false ? ' (inaktiv)' : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                <CheckboxGroup
                  label="Prüfungsarten"
                  options={INSPECTION_TYPES}
                  values={criteria.inspectionTypes}
                  onToggle={value =>
                    updateCriteria(
                      'inspectionTypes',
                      toggleArrayValue(criteria.inspectionTypes, value)
                    )
                  }
                />
                <CheckboxGroup
                  label="Artikelarten"
                  options={ITEM_TYPES}
                  values={criteria.itemTypes}
                  onToggle={value =>
                    updateCriteria(
                      'itemTypes',
                      toggleArrayValue(criteria.itemTypes, value)
                    )
                  }
                />
              </>
            )}

            <ReportDataPreview
              type={selectedType}
              data={previewData}
              loading={previewLoading}
              error={previewError}
              criteria={criteria}
            />
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.footer}>
          <button type="button" onClick={onClose} style={styles.secondaryButton}>
            Abbrechen
          </button>
          <div style={styles.footerRight}>
            {step === 2 && (
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError('');
                }}
                style={styles.secondaryButton}
              >
                Zurück
              </button>
            )}
            <button
              type="button"
              onClick={step === 1 ? goToCriteria : applySelection}
              style={styles.primaryButton}
            >
              {step === 1 ? 'Weiter' : 'Übernehmen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportDataPreview({ type, data, loading, error, criteria }) {
  let content;
  if (loading) {
    content = <div style={styles.previewStatus}>Lade Listendaten …</div>;
  } else if (error) {
    content = <div style={styles.previewError}>{error}</div>;
  } else if (type === 'selfPickup') {
    content = <SelfPickupPreview markers={data || []} date={criteria.date} />;
  } else if (type === 'transport') {
    content = <TransportPreview transports={data || []} date={criteria.date} />;
  } else if (type === 'workshopAssignment') {
    content = <WorkshopAssignmentPreview groupedLogs={data || {}} date={criteria.date} />;
  } else {
    content = <InspectionPreview inspections={data || []} />;
  }

  return (
    <section style={styles.previewSection} aria-live="polite">
      <h3 style={styles.previewTitle}>Listenvorschau</h3>
      <div style={styles.previewViewport}>{content}</div>
    </section>
  );
}

function SelfPickupPreview({ markers, date }) {
  if (!markers.length) {
    return (
      <div style={styles.previewStatus}>
        Für den {formatPreviewDate(date)} wurden keine Einträge gefunden.
      </div>
    );
  }

  return (
    <table style={styles.previewTable}>
      <thead>
        <tr>
          {['ID', 'Order-Nr.', 'Kunde', 'Artikel', 'Art', 'Datum', 'Zeit', 'Ort', 'Notizen']
            .map(label => <th key={label} style={styles.previewTh}>{label}</th>)}
        </tr>
      </thead>
      <tbody>
        {markers.map((marker, index) => (
          <tr key={`${marker.id}-${marker.relevantDateType}-${index}`}>
            <td style={styles.previewTd}>{marker.id}</td>
            <td style={styles.previewTd}>{marker.orderItem?.order?.orderNumber || '-'}</td>
            <td style={styles.previewTd}>{marker.orderItem?.order?.customer?.company || '-'}</td>
            <td style={styles.previewTd}>{marker.orderItem?.item?.name || '-'}</td>
            <td style={styles.previewTd}>{marker.relevantDateType || '-'}</td>
            <td style={styles.previewTd}>{formatPreviewDate(marker.relevantDate)}</td>
            <td style={styles.previewTd}>{formatPreviewTime(marker.relevantTime)}</td>
            <td style={styles.previewTd}>{marker.operationLocation || '-'}</td>
            <td style={styles.previewTd}>{marker.serviceNotes || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TransportPreview({ transports, date }) {
  if (!transports.length) {
    return (
      <div style={styles.previewStatus}>
        Für den {formatPreviewDate(date)} sind keine Transporte geplant.
      </div>
    );
  }

  const groupedByVehicle = transports.reduce((groups, tour) => {
    const vehicleName = tour.vehicle?.name || 'Unbekanntes Fahrzeug';
    if (!groups[vehicleName]) groups[vehicleName] = [];
    groups[vehicleName].push(tour);
    return groups;
  }, {});

  return Object.entries(groupedByVehicle).map(([vehicleName, tours]) => (
    <div key={vehicleName} style={styles.previewGroup}>
      <h4 style={styles.previewGroupTitle}>
        {vehicleName} – {tours[0]?.vehicle?.licensePlate || '-'}
      </h4>
      <table style={styles.previewTable}>
        <thead>
          <tr>
            {['Tour', 'Zeitraum', 'Fahrer', 'Marker', 'Firma', 'Artikel', 'Zeitpunkt', 'Ort', 'Notizen']
              .map(label => <th key={label} style={styles.previewTh}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {tours.flatMap((tour, tourIndex) =>
            (tour.markers || []).map((marker, markerIndex) => (
              <tr key={`${vehicleName}-${tourIndex}-${markerIndex}`}>
                <td style={styles.previewTd}>{tour.tourName || '-'}</td>
                <td style={styles.previewTd}>{tour.timeframeFrom || '-'} – {tour.timeframeTo || '-'}</td>
                <td style={styles.previewTd}>
                  {tour.driver
                    ? `${tour.driver.firstName || ''} ${tour.driver.lastName || ''}`.trim()
                    : '-'}
                </td>
                <td style={styles.previewTd}>{marker.markerType || '-'}</td>
                <td style={styles.previewTd}>{marker.orderItem?.order?.customer?.company || '-'}</td>
                <td style={styles.previewTd}>{marker.orderItem?.item?.name || '-'}</td>
                <td style={styles.previewTd}>{marker.sortTime || '-'}</td>
                <td style={styles.previewTd}>
                  {marker.markerType === 'pickup'
                    ? marker.operationLocationPickup || '-'
                    : marker.operationLocationDelivery || '-'}
                </td>
                <td style={styles.previewTd}>{marker.transportNotes || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  ));
}

function WorkshopAssignmentPreview({ groupedLogs, date }) {
  const employeeGroups = Object.entries(groupedLogs);
  if (!employeeGroups.length) {
    return (
      <div style={styles.previewStatus}>
        Für den {formatPreviewDate(date)} wurden keine Einträge gefunden.
      </div>
    );
  }

  return employeeGroups.map(([employeeName, entries]) => (
    <div key={employeeName} style={styles.previewGroup}>
      <h4 style={styles.previewGroupTitle}>{employeeName}</h4>
      <table style={styles.previewTable}>
        <thead>
          <tr>
            {[
              'ID', 'Auftrags-Nr.', 'Item', 'Notiz', 'Gepl. Start', 'Gepl. Ende',
              'Ist: Start', 'Ist: Ende', 'Pause', 'Fertig?', 'Kunde',
            ].map(label => <th key={label} style={styles.previewTh}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {entries.map(log => (
            <tr key={log.id}>
              <td style={styles.previewTd}>{log.id}</td>
              <td style={styles.previewTd}>{log.workshopOrderNr || '-'}</td>
              <td style={styles.previewTd}>{log.itemName || '-'}</td>
              <td style={styles.previewTd}>{log.notes || '-'}</td>
              <td style={styles.previewTd}>{log.plannedStart || '-'}</td>
              <td style={styles.previewTd}>{log.plannedEnd || '-'}</td>
              <td style={styles.previewTd}>{log.actualStart || '-'}</td>
              <td style={styles.previewTd}>{log.actualEnd || '-'}</td>
              <td style={styles.previewTd}>{log.pauseTime ?? '-'}</td>
              <td style={styles.previewTd}>{log.finished || '-'}</td>
              <td style={styles.previewTd}>{log.customer || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ));
}

function InspectionPreview({ inspections }) {
  if (!inspections.length) {
    return <div style={styles.previewStatus}>Keine Inspektionen im gewählten Zeitraum gefunden.</div>;
  }

  return (
    <table style={styles.previewTable}>
      <thead>
        <tr>
          {[
            'Prüfung', 'Datum', 'Name', 'Lagerort', 'Typ', 'Hersteller', 'Modell',
            'Baujahr', 'Seriennr.', 'Inventarnr.', 'LW-ID', 'Kennzeichen',
          ].map(label => <th key={label} style={styles.previewTh}>{label}</th>)}
        </tr>
      </thead>
      <tbody>
        {inspections.map((inspection, index) => (
          <tr key={`${inspection.id || inspection.itemId}-${inspection.inspectionType}-${inspection.inspectionDate}-${index}`}>
            <td style={styles.previewTd}>{inspection.inspectionType}</td>
            <td style={styles.previewTd}>{formatPreviewDate(inspection.inspectionDate)}</td>
            <td style={styles.previewTd}>{inspection.name || '-'}</td>
            <td style={styles.previewTd}>{inspection.storageLocation?.name || '-'}</td>
            <td style={styles.previewTd}>{inspection.itemType || '-'}</td>
            <td style={styles.previewTd}>{inspection.manufacturer || '-'}</td>
            <td style={styles.previewTd}>{inspection.manufacturerType || '-'}</td>
            <td style={styles.previewTd}>{inspection.yearOfManufacture || '-'}</td>
            <td style={styles.previewTd}>{inspection.serialNumber || '-'}</td>
            <td style={styles.previewTd}>{inspection.inventoryNumber || '-'}</td>
            <td style={styles.previewTd}>{inspection.liftworkerId || '-'}</td>
            <td style={styles.previewTd}>{inspection.licensePlate || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function CheckboxGroup({ label, options, values, onToggle }) {
  return (
    <fieldset style={styles.fieldset}>
      <legend style={styles.legend}>{label}</legend>
      <div style={styles.checkboxGrid}>
        {options.map(option => (
          <label key={option.value} style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={values.includes(option.value)}
              onChange={() => onToggle(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    zIndex: 2000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  modal: {
    width: 'min(1180px, 100%)',
    maxHeight: '92vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '14px',
    marginBottom: '18px',
  },
  title: { margin: 0, fontSize: '24px' },
  stepText: { color: '#64748b', fontSize: '14px', marginTop: '4px' },
  closeButton: {
    border: 0,
    background: 'transparent',
    fontSize: '30px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#475569',
  },
  intro: { margin: '0 0 16px', color: '#334155' },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '12px',
  },
  reportCard: {
    textAlign: 'left',
    padding: '16px',
    border: '1px solid #cbd5e1',
    borderRadius: '9px',
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    color: '#0f172a',
  },
  reportCardSelected: {
    border: '2px solid #2563eb',
    background: '#eff6ff',
    padding: '15px',
  },
  cardDescription: { color: '#64748b', fontSize: '14px', lineHeight: 1.4 },
  row: { display: 'flex', gap: '14px', flexWrap: 'wrap' },
  field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '6px',
    flex: '1 1 220px',
    marginBottom: '14px',
    textAlign: 'left',
  },
  fieldLabel: { fontWeight: 600, color: '#334155' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    padding: '9px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    background: '#fff',
  },
  fieldset: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    margin: '0 0 14px',
  },
  legend: { fontWeight: 600, color: '#334155', padding: '0 6px' },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
    gap: '9px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    textAlign: 'left',
  },
  previewSection: {
    marginTop: '20px',
    border: '1px solid #dbe3ee',
    borderRadius: '9px',
    background: '#fff',
  },
  previewTitle: {
    margin: 0,
    padding: '12px 14px',
    borderBottom: '1px solid #dbe3ee',
    background: '#f8fafc',
    borderRadius: '9px 9px 0 0',
    color: '#1e293b',
    fontSize: '17px',
  },
  previewViewport: {
    maxHeight: '420px',
    overflow: 'auto',
  },
  previewStatus: {
    padding: '18px',
    color: '#475569',
  },
  previewError: {
    padding: '18px',
    color: '#b91c1c',
    background: '#fef2f2',
  },
  previewGroup: {
    padding: '0 12px 12px',
    minWidth: 'max-content',
  },
  previewGroupTitle: {
    margin: '14px 0 8px',
    paddingBottom: '5px',
    borderBottom: '1px solid #94a3b8',
    color: '#334155',
  },
  previewTable: {
    width: '100%',
    minWidth: '900px',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  previewTh: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '7px 8px',
    border: '1px solid #d1d5db',
    background: '#f1f5f9',
    color: '#334155',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  previewTd: {
    padding: '7px 8px',
    border: '1px solid #d1d5db',
    color: '#1f2937',
    verticalAlign: 'top',
  },
  error: {
    marginTop: '16px',
    padding: '10px 12px',
    borderRadius: '6px',
    background: '#fef2f2',
    color: '#b91c1c',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    borderTop: '1px solid #e5e7eb',
    marginTop: '22px',
    paddingTop: '16px',
  },
  footerRight: { display: 'flex', gap: '10px' },
  primaryButton: {
    border: 0,
    borderRadius: '6px',
    padding: '9px 16px',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  },
  secondaryButton: {
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '9px 16px',
    background: '#fff',
    color: '#334155',
    cursor: 'pointer',
  },
};

export default ReportSelectionModal;
